import { Box, Button, Divider, Stack } from '@mui/material'
import ComboBox from 'Components/ComboBox'
import LanguageSelector from 'Components/LanguageSelector' // LAP-733 (GE022)
import React, { useEffect, useMemo } from 'react'
import { useForm } from 'react-hook-form'
import { useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next' // LAP-733 (GE022)
import { SUPPORTED_LANGUAGES, LANGUAGE_META } from '../../i18n' // LAP-733 (GE022)
// Fix lint error
import { makePostRequest, makeGetRequest } from 'utils/api'

export default function LangueRegion({ appUser, setAppUser, handleClose, fetchUser }) {
  const { t, i18n: i18nInstance } = useTranslation('layout')
  const [selectedLangue, setSelectedLangue] = React.useState(null)
  const [selectedFormat, setSelectedFormat] = React.useState(null)
  const [selectedHour, setSelectedHour] = React.useState(null)
  const [selectedCurrency, setSelectedCurrency] = React.useState(null)
  const [currencyList, setCurrencyList] = React.useState([])

  const siteUser = useSelector((store) => store.user)

  // LAP-733 (GE022) — proper supported language list with native + translated
  // labels. The dropdown now reflects the user's actual UI language and the
  // option labels are translated to the active interface language.
  const langueList = useMemo(
    () =>
      SUPPORTED_LANGUAGES.map((code, idx) => {
        const meta = LANGUAGE_META[code] || {}
        const translated = t(`settings.languages.${code}`, {
          defaultValue: meta.native || code,
        })
        return {
          id: idx + 1,
          code,
          // ComboBox displays `label`. Show: 🇫🇷 Français (Französisch)
          label: meta.native
            ? `${meta.flag || ''} ${meta.native}${
                translated && translated !== meta.native ? ` (${translated})` : ''
              }`.trim()
            : translated,
          // We persist the ISO code on the user_parameter.langue field.
          value: code,
        }
      }),
    [t, i18nInstance.language],
  )

  useEffect(() => {
    getCurrencies()
  }, [])

  const getCurrencies = async () => {
    const res = await makeGetRequest('/api/Finance/devise-list/', {}, true)
    if (res.status === 200) {
      let data = []
      res.data.forEach((e) => {
        data.push({ ...e, label: `${e.Code_iso} | ${e.Designation}`, value: e.id })
      })
      setCurrencyList(data)
    }
  }

  useEffect(() => {
    let cur_user = siteUser.user?.user_parameter
    let user_currency_id = siteUser.user?.currency

    // LAP-733 (GE022) — bind the dropdown to the user's actual UI language.
    // Resolution order: i18nInstance.language → siteUser.user.language →
    // legacy user_parameter.langue (free text e.g. "Français") → 'fr'.
    const rawLang =
      (i18nInstance.language && String(i18nInstance.language).split('-')[0]) ||
      siteUser.user?.language ||
      cur_user?.langue ||
      'fr'
    const normalised = String(rawLang).toLowerCase()
    const legacyMap = {
      français: 'fr',
      francais: 'fr',
      french: 'fr',
      english: 'en',
      anglais: 'en',
      deutsch: 'de',
      allemand: 'de',
      german: 'de',
      italiano: 'it',
      italien: 'it',
      italian: 'it',
      español: 'es',
      espanol: 'es',
      espagnol: 'es',
      spanish: 'es',
    }
    const code = legacyMap[normalised] || normalised
    const match =
      langueList.find((e) => e.value === code) || langueList[0]
    setSelectedLangue(match)
    setValue('langue', match)
    setSelectedFormat(dateFormatList.filter((e) => e.value === cur_user?.format_de_date)[0])
    setValue(
      'format_de_date',
      dateFormatList.filter((e) => e.value === cur_user?.format_de_date)[0],
    )
    setSelectedHour(hourList.filter((e) => e.value === cur_user?.format_de_lheure)[0])
    setValue('format_de_lheure', hourList.filter((e) => e.value === cur_user?.format_de_lheure)[0])

    if (user_currency_id && currencyList.length > 0) {
      const currentCurrency = currencyList.find((c) => c.id === user_currency_id)
      if (currentCurrency) {
        setSelectedCurrency(currentCurrency)
        setValue('currency', currentCurrency)
      }
    }
    // LAP-733 (GE022) — re-sync when the UI language or the translated label
    // list changes so the dropdown follows the user's choice immediately.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currencyList, siteUser, i18nInstance.language, langueList])

  const { handleSubmit, setValue } = useForm({
    defaultValues: {},
  })
  const onSubmit = async (data) => {
    let payload = {
      format_de_lheure: data.format_de_lheure.value,
      format_de_date: data.format_de_date.value,
      langue: data.langue.value,
      currency: data.currency ? data.currency.id : null,
    }

    const response = await makePostRequest('/api/user/update-aspect/', payload, {}, true)
    if (response.status === 200) {
      alert(t('settings.languageRegion.updateSuccess'))
      fetchUser()
    } else {
      alert(t('settings.languageRegion.updateError'))
    }
  }

  const dateFormatList = [
    { id: 1, label: 'dd.MM.yyy', value: 'dd.MM.yyy' },
    { id: 2, label: 'MM.dd.yyyy', value: 'MM.dd.yyyy' },
    { id: 3, label: 'yyyy.MM.dd', value: 'yyyy.MM.dd' },
    { id: 4, label: 'yyyy.dd.MM', value: 'yyyy.dd.MM' },
  ]
  const hourList = [
    { id: 1, label: '12h', value: '12h' },
    { id: 2, label: '24h', value: '24h' },
  ]
  return (
    <form onSubmit={handleSubmit(onSubmit)} style={{ height: '100%' }}>
      <Box className="sdf" display={'flex'} flexDirection={'column'} height={'100%'}>
        <Box sx={{ width: '100%' }} className="hel">
          <Box sx={{ background: '#001B42', p: 3, display: 'flex', alignItems: 'center' }}>
            <Box ml={2} color={'white'}>
              {t('settings.languageRegion.title')}
            </Box>
          </Box>
          <Box paddingX={4}>
            {/* LAP-733 (GE022) — UI language picker */}
            <Box pt={3} pb={2}>
              <LanguageSelector value={siteUser?.user?.language} />
            </Box>
            <Divider />
            <Box
              display={'flex'}
              justifyContent={'center'}
              flexDirection={'column'}
              alignItems={'center'}
              pt={2}
            >
              <Box display={'flex'} justifyContent={'center'} alignItems={'center'}>
                <label style={{ width: '160px' }}>{t('settings.languageRegion.langueLabel')}</label>

                <ComboBox
                  handleAutoComplete={(e, j) => {
                    setValue('langue', j)
                    setSelectedLangue(j)
                  }}
                  data={langueList}
                  name="langue"
                  required={true}
                  selectedValue={selectedLangue}
                />
              </Box>
              <Box display={'flex'} justifyContent={'center'} alignItems={'center'}>
                <label style={{ width: '160px' }}>{t('settings.languageRegion.dateFormatLabel')}</label>

                <ComboBox
                  handleAutoComplete={(e, j) => {
                    setValue('format_de_date', j)
                    setSelectedFormat(j)
                  }}
                  data={dateFormatList}
                  name="format_de_date"
                  required={true}
                  selectedValue={selectedFormat}
                />
              </Box>
              <Box display={'flex'} justifyContent={'center'} alignItems={'center'}>
                <label style={{ width: '160px' }}>{t('settings.languageRegion.hourFormatLabel')}</label>

                <ComboBox
                  handleAutoComplete={(e, j) => {
                    setValue('format_de_lheure', j)
                    setSelectedHour(j)
                  }}
                  data={hourList}
                  name="format_de_lheure"
                  required={true}
                  selectedValue={selectedHour}
                />
              </Box>
              <Box display={'flex'} justifyContent={'center'} alignItems={'center'}>
                <label style={{ width: '160px' }}>{t('settings.languageRegion.currencyLabel')}</label>

                <ComboBox
                  handleAutoComplete={(e, j) => {
                    setValue('currency', j)
                    setSelectedCurrency(j)
                  }}
                  data={currencyList}
                  name="currency"
                  required={false}
                  selectedValue={selectedCurrency}
                />
              </Box>
            </Box>
          </Box>
        </Box>
        <Box sx={{ marginTop: 'auto', background: '#001B42', py: 1, pr: 2 }}>
          <Stack spacing={2} direction="row" justifyContent={'flex-end'}>
            <Button type="submit" variant="text" sx={{ color: 'white', textTransform: 'none' }}>
              {t('settings.languageRegion.save')}
            </Button>
            <Button
              variant="text"
              sx={{ color: 'white', textTransform: 'none' }}
              onClick={handleClose}
            >
              {t('settings.languageRegion.cancel')}
            </Button>
          </Stack>
        </Box>
      </Box>
    </form>
  )
}
